<template>
    <div class="relative">
        <BaseButton
            class="text-theme-toolbar-icon"
            data-action="copy"
            dusk="selectionCitationMenuButton"
            :tooltip="localizations[lang].tooltips.citation"
            :btn-text="btnText"
            icon="quote-24px"
            @click="toggleCopyMenu"
        />
        <transition name="slide-fade">
            <ListDropdown v-show="menuCopyIsShowing" :submenu-display-in-down="submenuDisplayInDown">
                <template v-for="format in availableCitationFormats">
                    <ChicagoSubmenuDropdown
                        v-if="format === 'Chicago'"
                        :key="format"
                        ref="chicagoDropdown"
                        :subformats="chicagoSubformats"
                        :labels="localizations[lang].citation.menu"
                        dusk-prefix="selectionCitationFormat-"
                        submenu-position-class="left-full ml-3"
                        chevron-position="right"
                        @select="copyCitationToClipboard"
                    />
                    <!-- Regular citation formats -->
                    <ListItemButton
                        v-else
                        :key="format"
                        :dusk="`selectionCitationFormat-${format}`"
                        class="text-theme-toolbar-icon"
                        @click="copyCitationToClipboard(format)"
                    >
                        {{ localizations[lang].citation.menu[format] }}
                    </ListItemButton>
                </template>
                <ListItemButton class="text-theme-toolbar-icon" @click="copyPlainToClipboard">
                    {{ localizations[lang].onlyCopy }}
                </ListItemButton>
            </ListDropdown>
        </transition>
    </div>
</template>

<script>
import BaseButton from '../../Components/buttons/BaseButton.vue';
import ListDropdown from '../../Components/list/ListDropdown.vue';
import ListItemButton from '../../Components/list/ListItemButton.vue';
import ChicagoSubmenuDropdown from '../../Components/list/ChicagoSubmenuDropdown.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import citationFormats, { getAvailableFormats } from '_shared_/js/utils/citationFormats.js';

export default {
    components: {
        BaseButton,
        ListDropdown,
        ListItemButton,
        ChicagoSubmenuDropdown,
    },
    props: {
        menuCopyIsShowing: {
            type: Boolean,
            required: true,
        },
        submenuDisplayInDown: {
            type: Boolean,
            required: true,
        },
        btnText: {
            type: String,
            required: false,
            default: null,
        },
    },
    emits: ['toggle-copy-menu'],
    data() {
        return {
            citationWasCopied: false,
        };
    },
    computed: {
        ...mapGetters(['currentLocation', 'isEpub']),
        ...mapState(['issue', 'title', 'localizations', 'currentHighlight', 'lang', 'aiActions', 'isOffline']),

        iconColorStyle() {
            return {
                color: this.currentHighlight.color ? this.currentHighlight.color : 'transparent',
            };
        },

        /**
         * Returns the filtered Chicago subformats based on issue metadata.
         * @returns {string[]}
         */
        chicagoSubformats() {
            return getAvailableFormats(citationFormats.chicagoSubformats, this.issue, this.title);
        },

        /**
         * Returns the list of available citation formats based on issue metadata.
         * Excludes Chicago when no subformat passes validation.
         * @returns {string[]}
         */
        availableCitationFormats() {
            const formats = getAvailableFormats(citationFormats.formats, this.issue, this.title);

            if (this.chicagoSubformats.length === 0) {
                return formats.filter(format => format !== 'Chicago');
            }

            return formats;
        },
    },
    watch: {
        menuCopyIsShowing(isOpen) {
            if (!isOpen) {
                const chicagoRefs = this.$refs.chicagoDropdown;
                const chicagoDropdown = Array.isArray(chicagoRefs) ? chicagoRefs[0] : chicagoRefs;
                chicagoDropdown?.hideSubmenu();
            }
        },
    },
    methods: {
        ...mapMutations(['openToast']),
        ...mapActions(['copyToClipBoard', 'trackEvent']),

        /**
         * Copies citation to clipboard.
         * @param {string} format - The citation format to use
         */
        copyCitationToClipboard(format) {
            if (this.citationWasCopied) return;

            const selectionText = citationFormats[format](
                this.currentHighlight.totalText,
                this.issue,
                this.currentLocation,
                this.title,
                this.isEpub,
                this.localizations[this.lang]
            );
            this.copyToClipBoard(selectionText);

            this.toggleCopyMenu();
            this.citationWasCopied = true;
            this.openToast({ text: this.localizations[this.lang].citation.messages.copied });

            this.trackEvent({
                action: `Citation - ${format}`,
                trigger: 'click',
                page: '',
                source: 'selection tool bar',
            });

            setTimeout(() => {
                this.citationWasCopied = false;
            }, 1000);
        },

        copyPlainToClipboard() {
            if (this.plainCopied) return;
            this.copyToClipBoard(this.currentHighlight.totalText);

            this.toggleCopyMenu();

            this.plainCopied = true;
            this.openToast({ text: this.localizations[this.lang].plainCopied });

            setTimeout(() => {
                this.plainCopied = false;
            }, 1000);
        },

        toggleCopyMenu() {
            this.$emit('toggle-copy-menu');
        },
    },
};
</script>
